import React, {
    useState
} from 'react';
import {
    Input
} from 'shared/components/ui/input';
import {
    Plus, X
} from 'lucide-react';

/**
 * ChipEditor — list-of-strings entry. Add via Enter, remove via X.
 * Per A-0012.02 SPEC §2.2 + UI-MAPPING.md (used for tags, pii_handling,
 * compliance_tags, context_keys, propensity_keys, governance.allowed/denied,
 * memory.governance.retain/forget, etc.).
 *
 * impliedChips: [{ value: string, tooltip: string }]
 *   Ghost chips rendered with dashed border + muted style. Clicking one
 *   promotes it into the real declared list via onChange. Chips already
 *   present in value are not shown as ghost (already declared).
 */
export default function ChipEditor({
    value = [],
    onChange,
    placeholder = 'Type and press Enter…',
    disabled = false,
    impliedChips = []
}) {
    const [draft, setDraft] = useState('');

    const items = Array.isArray(value) ? value : [];

    function commit() {
        const v = draft.trim();
        if (!v) return;
        if (items.includes(v)) {
            setDraft('');
            return;
        }
        onChange?.([...items, v]);
        setDraft('');
    }

    function remove(idx) {
        onChange?.(items.filter((_, i) => i !== idx));
    }

    function promote(chipValue) {
        if (items.includes(chipValue)) return;
        onChange?.([...items, chipValue]);
    }

    const pendingImplied = impliedChips.filter((c) => !items.includes(c.value));

    return (
        <div className="flex flex-wrap items-center gap-1.5">
            {items.map((it, i) => (
                <span
                    key={`declared-${it}-${i}`}
                    className="inline-flex items-center gap-1 rounded-full border bg-secondary px-2.5 py-0.5 text-xs"
                >
                    {it}
                    {!disabled && (
                        <button
                            type="button"
                            onClick={() => remove(i)}
                            className="text-muted-foreground hover:text-destructive"
                            aria-label={`Remove ${it}`}
                        >
                            <X className="h-3 w-3" />
                        </button>
                    )}
                </span>
            ))}
            {pendingImplied.map((c) => (
                <button
                    key={`implied-${c.value}`}
                    type="button"
                    title={c.tooltip}
                    onClick={() => !disabled && promote(c.value)}
                    className="inline-flex items-center gap-1 rounded-full border border-dashed border-muted-foreground/40 bg-transparent px-2.5 py-0.5 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary disabled:pointer-events-none"
                    disabled={disabled}
                >
                    <Plus className="h-2.5 w-2.5 flex-shrink-0" />
                    {c.value}
                </button>
            ))}
            {!disabled && (
                <Input
                    type="text"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            commit();
                        } else if (e.key === 'Backspace' && !draft && items.length > 0) {
                            remove(items.length - 1);
                        }
                    }}
                    onBlur={commit}
                    placeholder={placeholder}
                    className="h-7 w-auto min-w-[140px] flex-1 text-xs"
                />
            )}
        </div>
    );
}
